import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import QueryBuilderIcon from '@mui/icons-material/QueryBuilder';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getAuth } from 'firebase/auth';
import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useStoreSelector } from 'v3/data/store';
import { setAllStoresIds, setStoreIdsListFilter } from 'v3/data/store/contracting/actions';
import { showErrorDialog } from 'v3/data/store/error/actions';
import { showSnackbar } from 'v3/data/store/snackbar/actions';
import { getStorePermissions, setStoreList } from 'v3/data/store/stores/actions';
import { IStoreItem } from 'v3/data/store/stores/types';
import { DownloadSsePayload, DownloadStatusEnum } from 'v3/hooks/types';
import { useBreakpoints } from 'v3/hooks/useBreakpoints';
import { useSseConnect } from 'v3/hooks/useSseConnect';
import * as api from 'v3/services/api';
import { CircularButton } from 'v3/shared/components/Button/CircularButton';
import { FlexButton } from 'v3/shared/components/Button/FlexButton';
import { MiniButton } from 'v3/shared/components/Button/MiniButton';
import { MainFilters } from 'v3/shared/components/Filters/MainFilters';
import { ExportModal } from 'v3/shared/components/Modal/ExportModal';
import { ContractingTable } from 'v3/shared/components/Table/ContractingTable';
import { ContractingFileTypeEnum } from 'v3/shared/enums/contracting-file-type.enum';
import { FeatureFlagEnum } from 'v3/shared/enums/feature-flag.enum';
import { CommonTemplate } from 'v3/shared/templates/CommonTemplate';
import { HeadlineMedium } from 'v3/shared/typography/HeadlineMedium';
import { theme } from 'v3/styles/global-styles';
import {
  AlignButtons,
  AlignMobileButtons,
  ClockColor,
  Container,
  Content,
  Header,
  IconColor,
  TitleContainer,
} from './styles';

export const ContractingListPage = () => {
  const { isMobile } = useBreakpoints();
  const { list, listView } = useStoreSelector((store) => store.contracting);
  const { storeList } = useStoreSelector((store) => store.store);
  const { group, profileId, id, storeUserId } = useStoreSelector((store) => store.store.storeLogged);
  const { isMaster, isGroupAdmin } = getStorePermissions();
  const { filter } = useStoreSelector((store) => store.contracting);
  const { initialStoreIds } = useStoreSelector((store) => store.contracting.generateAllContractingList);
  const [token, setToken] = useState<string>('');
  const [openModal, setOpenModal] = useState(false);
  const [exportAllContractListFeatureFlagEnabled, setExportAllContractListFeatureFlagEnabled] = useState(false);

  const { startSse, reconnect, isSseConnected, setIsSseConnected, saveSeeToken, getValidSeeToken, removeSeeToken } =
    useSseConnect<DownloadSsePayload>({
      shouldCloseConnection: (data) => data.downloadStatus === DownloadStatusEnum.COMPLETED,
    });

  const fetchExportAllContractListFeatureFlag = useCallback(async () => {
    const response = await api.getFlagStatus(FeatureFlagEnum.IKB2C_10334_EXPORT_ALL_CONTRACT_LIST);
    setExportAllContractListFeatureFlagEnabled(response.data.enabled);
  }, []);

  useEffect(() => {
    fetchExportAllContractListFeatureFlag();
  }, []);

  const extractStoreIds = (data: IStoreItem[]) => {
    return data.map((store) => store.id);
  };

  const handleOpenBackofficeHistoryPage = () => {
    const url = import.meta.env.VITE_BACKOFFICE_HISTORY_LINK;
    window.open(url, '_blank');
  };

  const fetchStoreIdsList = useCallback(async () => {
    try {
      const response = await api.getStores({ group, profileId, storeUserId });
      const storeIds = extractStoreIds(response.data);
      setStoreIdsListFilter(storeIds);
      const storeListFormatted = response.data.map((item) => ({
        ...item,
        checked: true,
      }));

      setAllStoresIds(storeListFormatted);
      setStoreList(storeListFormatted);
    } catch (error) {
      showErrorDialog('Erro ao obter lista de lojas', error);
    }
  }, [id, group]);

  useEffect(() => {
    const shouldHasStoreIds = (isMaster || isGroupAdmin) && filter.storeIds.isEmpty;

    if (id.isNotEmpty && group.isNotEmpty && shouldHasStoreIds) fetchStoreIdsList();
  }, [fetchStoreIdsList]);

  const isListEmpty = exportAllContractListFeatureFlagEnabled ? listView.items.length === 0 : list.items.length === 0;

  const getUserAuthToken = useCallback(async () => {
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken();
    setToken(token || '');
  }, []);

  useEffect(() => {
    getUserAuthToken();
  }, [getUserAuthToken]);

  const buildSseUrl = useCallback(
    (selectedFormat: string, isSelectedFiltered: boolean, sseToken: string) => {
      const queryParams = new URLSearchParams();
      queryParams.append('fileType', selectedFormat);
      queryParams.append('sseToken', sseToken);

      getFilters(isSelectedFiltered, queryParams);

      return `/contracting/generate-file-link/?${queryParams.toString()}`;
    },
    [filter, storeList, id],
  );

  const getFilters = (isSelectedFiltered: boolean, queryParams: URLSearchParams) => {
    if (isSelectedFiltered) {
      Object.entries(filter).forEach(([key, value]) => {
        if (key !== 'storeIds' && value && value.length > 0) {
          queryParams.append(key, value);
        }
      });
    }
  };

  const getStoreIdsFilter = (isSelectedFiltered: boolean): string[] => {
    if (!isSelectedFiltered) {
      return initialStoreIds.length > 0 ? [...initialStoreIds.map((store) => store.id), id] : [id];
    } else {
      return filter.storeIds.length > 0 ? [...filter.storeIds, id] : [id];
    }
  };

  const handleCacheStoreIdsFilter = async (sseToken: string, storeIds: string[]) => {
    try {
      await api.cacheStoreIdsFilters(sseToken, { storeIds });
    } catch (error) {
      showSnackbar('Erro interno', 'error', true);
      console.error(error);
    }
  };

  const onSubmit = async (selectedFormat: string, isSelectedFiltered: boolean) => {
    setOpenModal(false);
    if (!token) {
      showErrorDialog('Error', 'Authentication token not found');
      return;
    }
    const sseToken = uuidv4();
    saveSeeToken(sseToken);
    const storeIds = getStoreIdsFilter(isSelectedFiltered);

    try {
      await handleCacheStoreIdsFilter(sseToken, storeIds);

      const url = buildSseUrl(selectedFormat, isSelectedFiltered, sseToken);

      startSse(url, (data) => {
        handleSseComplete(data, selectedFormat);
      });
    } catch (error) {
      showSnackbar('Erro interno. Tente novamente mais tarde', 'error', true);
      console.error(error);
    }
  };

  useEffect(() => {
    const sseTokenLocalStorage = getValidSeeToken();
    if (sseTokenLocalStorage) {
      reconnect(buildSseUrl(ContractingFileTypeEnum.PDF, false, sseTokenLocalStorage), handleSseComplete);
    }
  }, []);

  const handleSseComplete = useCallback((data?: any, selectedFormat?: string) => {
    if (data.downloadStatus === DownloadStatusEnum.COMPLETED) {
      removeSeeToken();
      window.open(data.url, '_blank');
    } else if (data.downloadStatus === DownloadStatusEnum.FAILED) {
      setIsSseConnected(false);
      removeSeeToken();
    }
  }, []);

  return (
    <CommonTemplate>
      <Container>
        <Content>
          <Header>
            <TitleContainer>
              <HeadlineMedium>Lista de contratações</HeadlineMedium>
            </TitleContainer>
            {isMobile ? (
              <AlignMobileButtons>
                <CircularButton
                  startIcon={ClockColor}
                  handleClick={handleOpenBackofficeHistoryPage}
                  backgroundColor={theme.palette.secondary.light}
                />
                {!isListEmpty && (
                  <CircularButton
                    startIcon={IconColor}
                    handleClick={() => setOpenModal(true)}
                    backgroundColor={theme.palette.secondary.light}
                    isLoading={isSseConnected}
                    disabled={isSseConnected}
                  />
                )}
                <CircularButton startIcon={RefreshIcon} handleClick={() => window.location.reload()} />
              </AlignMobileButtons>
            ) : (
              <AlignButtons>
                <MiniButton
                  icon={QueryBuilderIcon}
                  $backgroundColor="transparent"
                  $outline
                  handleClick={handleOpenBackofficeHistoryPage}
                />
                {!isListEmpty && (
                  <MiniButton
                    $backgroundColor="transparent"
                    icon={FileDownloadOutlinedIcon}
                    $outline
                    handleClick={() => setOpenModal(true)}
                    isLoading={isSseConnected}
                    disabled={isSseConnected}
                  />
                )}
                <FlexButton
                  size="small"
                  endIcon={RefreshIcon}
                  handleClick={() => window.location.reload()}
                  text={'Atualizar'}
                />
              </AlignButtons>
            )}
          </Header>
          <MainFilters />
          <ContractingTable />
        </Content>
        <ExportModal open={openModal} setOpenModal={setOpenModal} list={list} />
      </Container>
    </CommonTemplate>
  );
};
